import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Calculator, Image, ChevronDown, Menu, X, Heart, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  navigate: (path: string) => void;
  scrollToBiodataCreator: () => void;
}

// Modern mobile menu overlay
const MobileMenu = ({ open, onClose, navigate, scrollToBiodataCreator }: MobileMenuProps) => (
  <div className={`fixed inset-0 z-[60] flex justify-end md:hidden ${open ? 'pointer-events-auto' : 'pointer-events-none'}`}>
    {/* Backdrop */}
    <div 
      className={`fixed inset-0 bg-black/60 transition-opacity duration-300 ease-in-out ${open ? 'opacity-100' : 'opacity-0'}`} 
      onClick={onClose}
    />
    
    {/* Sidebar Panel */}
    <div 
      className={`relative w-[85vw] sm:w-80 h-[100dvh] bg-white shadow-[0_0_15px_rgba(0,0,0,0.1)] p-6 flex flex-col border-l border-[#e0e0e0] transition-transform duration-300 ease-in-out transform ${open ? 'translate-x-0' : 'translate-x-full'}`}
      style={{ backgroundColor: '#ffffff' }}
    >
      <div className="flex items-center justify-between mb-8">
        <img
          src="/Media/Website Images/Main Biodata360 Logo.png"
          alt="BioData360 Logo"
          className="h-10 w-auto cursor-pointer transition-transform hover:scale-105"
          onClick={() => { navigate('/'); onClose(); }}
        />
        <button 
          onClick={onClose} 
          className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
          aria-label="Close menu"
        >
          <X className="h-6 w-6 text-[#333333]" />
        </button>
      </div>
      <nav className="flex-1 flex flex-col gap-3 overflow-y-auto min-h-0 w-full">
        <button
          onClick={() => { navigate('/'); onClose(); }}
          className="text-[#333333] text-lg font-semibold text-left py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors duration-200"
        >
          Home
        </button>

        <div className="bg-gray-50 rounded-xl p-4 border border-[#e0e0e0]/50">
          <div className="text-[#333333] text-lg font-semibold mb-3">Tools</div>
          <div className="space-y-2">
            <button
              onClick={() => { navigate('/dowry-calculator'); onClose(); }}
              className="flex items-center gap-3 text-gray-600 hover:text-red-600 py-3 px-3 w-full text-left rounded-lg hover:bg-white transition-all duration-200 group"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform duration-200">
                <Calculator className="h-4 w-4 text-white" />
              </div>
              <span className="font-medium text-[#333333]">Dowry Calculator</span>
            </button>
            <button
              onClick={() => { navigate('/partner-compatibility'); onClose(); }}
              className="flex items-center gap-3 text-gray-600 hover:text-red-600 py-3 px-3 w-full text-left rounded-lg hover:bg-white transition-all duration-200 group"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform duration-200">
                <Heart className="h-4 w-4 text-white" />
              </div>
              <span className="font-medium text-[#333333]">Partner Compatibility</span>
            </button>
            <button
              onClick={() => { navigate('/biodata-profile-maker'); onClose(); }}
              className="flex items-center gap-3 text-gray-600 hover:text-red-600 py-3 px-3 w-full text-left rounded-lg hover:bg-white transition-all duration-200 group"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform duration-200">
                <Image className="h-4 w-4 text-white" />
              </div>
              <span className="font-medium text-[#333333]">Biodata Profile Maker</span>
            </button>
            <button
              onClick={() => { navigate('/wedding-hashtag-generator'); onClose(); }}
              className="flex items-center gap-3 text-gray-600 hover:text-red-600 py-3 px-3 w-full text-left rounded-lg hover:bg-white transition-all duration-200 group"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform duration-200">
                <Hash className="h-4 w-4 text-white" />
              </div>
              <span className="font-medium text-[#333333]">Wedding Hashtag Generator</span>
            </button>
          </div>
        </div>

        <button
          onClick={() => { navigate('/blog'); onClose(); }}
          className="text-[#333333] text-lg font-semibold text-left py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors duration-200"
        >
          Blog
        </button>
        <button
          onClick={() => { navigate('/contact-us'); onClose(); }}
          className="text-[#333333] text-lg font-semibold text-left py-3 px-4 rounded-xl hover:bg-gray-50 transition-colors duration-200"
        >
          Contact
        </button>

        <Button
          onClick={() => { scrollToBiodataCreator(); onClose(); }}
          className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold px-6 py-3 text-base tracking-wide mt-4 rounded-full shadow-md hover:shadow-lg transition-all duration-200"
        >
          CREATE BIODATA
        </Button>
      </nav>
    </div>
  </div>
);

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const homeRef = useRef<HTMLButtonElement>(null);
  const toolsRef = useRef<HTMLButtonElement>(null);
  const blogRef = useRef<HTMLButtonElement>(null);
  const contactRef = useRef<HTMLButtonElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

  const scrollToBiodataCreator = () => {
    const section = document.getElementById('biodata-creator');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        const section = document.getElementById('biodata-creator');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const activeKey = useMemo(() => {
    if (toolsOpen) return "tools";
    const path = location.pathname;
    if (path === "/") return "home";
    if (path.startsWith("/blog")) return "blog";
    if (path.startsWith("/contact-us")) return "contact";
    if (
      path.startsWith("/dowry-calculator") ||
      path.startsWith("/partner-compatibility") ||
      path.startsWith("/biodata-profile-maker") ||
      path.startsWith("/wedding-hashtag-generator")
    ) {
      return "tools";
    }
    return "home";
  }, [location.pathname, toolsOpen]);

  const updateIndicator = () => {
    const container = navContainerRef.current;
    if (!container) return;

    const refs: Record<string, React.RefObject<HTMLButtonElement>> = {
      home: homeRef,
      tools: toolsRef,
      blog: blogRef,
      contact: contactRef,
    };

    const target = refs[activeKey]?.current;
    if (!target) return;

    const cRect = container.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();
    const left = tRect.left - cRect.left;
    const width = tRect.width;
    setIndicator({ left, width });
  };

  useLayoutEffect(() => {
    updateIndicator();
  }, [activeKey]);

  useEffect(() => {
    const container = navContainerRef.current;
    if (!container) return;

    const ro = new ResizeObserver(() => updateIndicator());
    ro.observe(container);
    window.addEventListener("resize", updateIndicator);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateIndicator);
    };
  }, [activeKey]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-white/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-4">
          {/* Logo - Left */}
          <div className="flex items-center">
            <img
              src="/Media/Website Images/Main Biodata360 Logo.png"
              alt="BioData360 Logo"
              className="h-12 w-auto cursor-pointer transition-transform hover:scale-105"
              onClick={() => navigate('/')}
            />
          </div>

          {/* Navigation - Center */}
          <nav className="hidden md:flex items-center">
            <div
              ref={navContainerRef}
              className="relative inline-flex items-center rounded-full border border-red-200 bg-white p-1 shadow-sm"
              onKeyDown={(e) => {
                const isLeft = e.key === "ArrowLeft";
                const isRight = e.key === "ArrowRight";
                if (!isLeft && !isRight) return;
                e.preventDefault();
                const order = ["home", "tools", "blog", "contact"] as const;
                const idx = order.indexOf(activeKey as any);
                const next = isLeft
                  ? order[(idx - 1 + order.length) % order.length]
                  : order[(idx + 1) % order.length];

                if (next === "home") navigate("/");
                if (next === "blog") navigate("/blog");
                if (next === "contact") navigate("/contact-us");
                if (next === "tools") setToolsOpen(true);

                setTimeout(() => {
                  if (next === "home") homeRef.current?.focus();
                  if (next === "tools") toolsRef.current?.focus();
                  if (next === "blog") blogRef.current?.focus();
                  if (next === "contact") contactRef.current?.focus();
                }, 0);
              }}
            >
              <div
                aria-hidden="true"
                className="absolute inset-y-1 rounded-full bg-gradient-to-r from-red-600 to-red-500 shadow-md transition-all duration-300 ease-out"
                style={{
                  left: `${indicator.left}px`,
                  width: `${indicator.width}px`,
                }}
              >
                <div className="absolute inset-0 overflow-hidden rounded-full">
                  <div className="absolute -left-1/3 top-0 h-full w-2/3 rotate-12 bg-white/20" />
                </div>
              </div>

              <button
                ref={homeRef}
                type="button"
                aria-current={activeKey === "home" ? "page" : undefined}
                onClick={() => navigate("/")}
                className={`relative z-10 px-4 py-2 rounded-full text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${
                  activeKey === "home" ? "text-white" : "text-red-300 hover:text-red-600"
                }`}
              >
                Home
              </button>

              <DropdownMenu open={toolsOpen} onOpenChange={setToolsOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    ref={toolsRef}
                    type="button"
                    aria-current={activeKey === "tools" ? "page" : undefined}
                    className={`relative z-10 flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${
                      activeKey === "tools" ? "text-white" : "text-red-300 hover:text-red-600"
                    }`}
                  >
                    Tools
                    <ChevronDown className="h-3 w-3 transition-transform duration-200 data-[state=open]:rotate-180" data-state={toolsOpen ? "open" : "closed"} />
                  </button>
                </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-64 bg-white/95 backdrop-blur-md border border-gray-200/50 shadow-xl rounded-2xl mt-2 p-2">
                    <DropdownMenuItem
                      onClick={() => navigate('/dowry-calculator')}
                      className="flex items-center space-x-3 px-4 py-3 hover:bg-red-50/80 transition-all duration-200 cursor-pointer group rounded-xl"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200 shadow-md">
                        <Calculator className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">Dowry Calculator</div>
                        <div className="text-xs text-gray-500">Calculate dowry estimates</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/partner-compatibility')}
                      className="flex items-center space-x-3 px-4 py-3 hover:bg-red-50/80 transition-all duration-200 cursor-pointer group rounded-xl"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200 shadow-md">
                        <Heart className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">Partner Compatibility</div>
                        <div className="text-xs text-gray-500">Analyze relationship compatibility</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/biodata-profile-maker')}
                      className="flex items-center space-x-3 px-4 py-3 hover:bg-red-50/80 transition-all duration-200 cursor-pointer group rounded-xl"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200 shadow-md">
                        <Image className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">Biodata Profile Maker</div>
                        <div className="text-xs text-gray-500">Edit photo & remove background</div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/wedding-hashtag-generator')}
                      className="flex items-center space-x-3 px-4 py-3 hover:bg-red-50/80 transition-all duration-200 cursor-pointer group rounded-xl"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200 shadow-md">
                        <Hash className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">Wedding Hashtag Generator</div>
                        <div className="text-xs text-gray-500">Create unique wedding hashtags</div>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

              <button
                ref={blogRef}
                type="button"
                aria-current={activeKey === "blog" ? "page" : undefined}
                onClick={() => navigate("/blog")}
                className={`relative z-10 px-4 py-2 rounded-full text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${
                  activeKey === "blog" ? "text-white" : "text-red-300 hover:text-red-600"
                }`}
              >
                Blog
              </button>
              <button
                ref={contactRef}
                type="button"
                aria-current={activeKey === "contact" ? "page" : undefined}
                onClick={() => navigate("/contact-us")}
                className={`relative z-10 px-4 py-2 rounded-full text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${
                  activeKey === "contact" ? "text-white" : "text-red-300 hover:text-red-600"
                }`}
              >
                Contact
              </button>
            </div>
          </nav>

          {/* CTA Button - Right */}
          <div className="flex items-center">
            <Button
              onClick={scrollToBiodataCreator}
              className="hidden md:flex bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold px-6 py-2.5 text-sm tracking-wide rounded-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            >
              CREATE BIODATA
            </Button>

            {/* Hamburger for mobile */}
            <button className="md:hidden ml-4 p-2 rounded-full hover:bg-gray-100/80 backdrop-blur-sm" onClick={() => setMobileOpen(true)}>
              <Menu className="h-6 w-6 text-gray-700" />
            </button>
          </div>
        </div>
      </div>
      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} navigate={navigate} scrollToBiodataCreator={scrollToBiodataCreator} />
    </header>
  );
};

export default Header;
